import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Tone } from "./labels";

type Schemas = components["schemas"];
export type Wallet = Schemas["StaffWallet"];
export type HolderType = Wallet["holder_type"];
export type WalletStatus = Wallet["status"];
export type StatementEntry = Schemas["WalletStatementEntry"];

export const HOLDER: Record<HolderType, { one: string; many: string }> = {
  customer: { one: "Customer", many: "Customers" },
  business: { one: "Business", many: "Businesses" },
  partner: { one: "Partner", many: "Partners" },
  driver: { one: "Driver", many: "Drivers" },
};

export const WALLET_STATUS: Record<WalletStatus, [label: string, tone: Tone]> = {
  active: ["Active", "success"],
  frozen: ["Frozen", "info"],
  closed: ["Closed", "neutral"],
};

export const ENTRY_KIND: Record<StatementEntry["kind"], string> = {
  delivery_completed: "Delivery completed",
  delivery_rerouted: "Reroute",
  payment_received: "Payment",
  refund: "Refund",
  invoice_issued: "Invoice",
  payout_paid: "Payout paid",
  wallet_top_up: "Top-up",
  wallet_transfer: "Transfer",
  adjustment: "Adjustment",
  reversal: "Reversal",
};

/** Where a holder's own page is: drivers live on Fleet. */
export function holderHref(h: Wallet["holder"]): string {
  if (h.kind === "customer") return `/customers/${h.id}`;
  if (h.kind === "business") return `/businesses/${h.id}`;
  if (h.kind === "partner") return `/partners/${h.id}`;
  return "/fleet?view=drivers";
}

/**
 * What a wallet's page offers. Freezing and the credit limit need `wallets.manage`; an
 * adjustment needs `ledger.adjust` and only applies to partner and driver wallets (customers get
 * refunds: `ADJUSTMENT_NOT_ALLOWED`). A closed wallet offers nothing.
 */
export function walletActionsFor(w: Pick<Wallet, "status" | "holder_type">, permissions: string[]) {
  const open = w.status !== "closed";
  const manage = open && permissions.includes("wallets.manage");
  return {
    freeze: manage && w.status === "active",
    unfreeze: manage && w.status === "frozen",
    creditLimit: manage,
    adjust:
      open && permissions.includes("ledger.adjust") && (w.holder_type === "partner" || w.holder_type === "driver"),
  };
}

export const walletQuery = (id: string) =>
  queryOptions({
    queryKey: ["wallet", id],
    queryFn: () => ok(api.GET("/v1/admin/wallets/{id}", { params: { path: { id } } })),
  });

const path = (id: string) => ({ params: { path: { id } } });

export const walletActions = {
  freeze: (id: string, reason: string) =>
    ok(api.POST("/v1/admin/wallets/{id}/freeze", { ...path(id), body: { reason } })),
  unfreeze: (id: string, reason: string) =>
    ok(api.POST("/v1/admin/wallets/{id}/unfreeze", { ...path(id), body: { reason } })),
  creditLimit: (id: string, credit_limit: string, reason: string) =>
    ok(api.PUT("/v1/admin/wallets/{id}/credit-limit", { ...path(id), body: { credit_limit, reason } })),
  adjust: (id: string, body: { direction: "credit" | "debit"; amount: string; reason: string }) =>
    ok(api.POST("/v1/admin/wallets/{id}/adjustments", { ...path(id), body })),
  /** A driver's cash float; it opens their wallet if they have none yet. */
  driverFloat: (driverId: string, credit_limit: string, reason: string) =>
    ok(
      api.PUT("/v1/admin/wallets/drivers/{driver_id}/credit-limit", {
        params: { path: { driver_id: driverId } },
        body: { credit_limit, reason },
      }),
    ),
  /** A holder's wallet, if they have one: for "Open their wallet" links. */
  of: async (holder: { driver_id: string } | { partner_id: string }) =>
    (await ok(api.GET("/v1/admin/wallets", { params: { query: { ...holder, limit: 1 } } }))).data[0] ?? null,
};
